import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { useEffect } from 'haunted'
import { QueryClient } from '@tanstack/query-core'
import { QueryController } from '@epermits/lit-query'
import { LitHauntedElement } from '../../mixins/lit-haunted-element';
import { FileReferenceAPI } from './FileReference';
import { isNullishOrWhitespace } from '../../functions/nullish';
import { formatBytes } from '../../functions/format_bytes';

import { stylesBootstrap } from '../../styles/bootstrap'
import { stylesFluentTable } from '../../styles/fluentTable'


@customElement('ep-file-reference-group-display')
export class EPFileReferenceGroupDisplay extends LitHauntedElement {
  
  @property({ attribute: 'api-origin', reflect: false })
  apiOrigin: string = window.origin;

  @property({ attribute: 'file-reference-group', reflect: true })
  fileReferenceGroupID?: string = '';

  // -----------------

  @state()
  provider?: FileReferenceAPI;

  groupQuery = new QueryController(this, () => ({
    queryKey: ['provider', this.provider ? 1 : 0, 'group', this.fileReferenceGroupID],
    queryFn: async () => {
      if (!this.provider) return;
      if (isNullishOrWhitespace(this.fileReferenceGroupID)) return;
      //console.log('Fetching files');

      const groupMeta = await this.provider.GetGroupMetadata(this.fileReferenceGroupID);
      const allFiles = await this.provider.GetManyFileReferenceMetadata(groupMeta.FileReferences);
      const completedFiles = allFiles.filter(f => f.Status === 'Complete' || f.Status === 'TemporaryDowntime');

      return {
        metadata: groupMeta,
        files: completedFiles,
      }
    },
  }), new QueryClient());

  //#region Event Handlers

  //#endregion

  //#region Renders

  render() {    
    //#region Effects

    // Whenever the API URL is updated, create a new provider
    useEffect(() => {
      try {
        const url = new URL(this.apiOrigin, window.origin);
        this.provider = new FileReferenceAPI(url);
      }
      catch {}
    }, [this.apiOrigin]);

    //#endregion

    //#region Main Render

    return html`
      <div>
      <table class="fluent">
        <thead>
          <tr>
            <th>File Name</th>
            <th>Creation Date</th>
            <th>File Size</th>
            <th style="width: 70px; text-align: right;">Actions</th>
          </tr>
        </thead>
        <tbody>
          ${
            this.groupQuery.result?.isPending
            ? html`
              <tr>
                <td colspan="4" style="text-align: center;">
                  <sl-spinner></sl-spinner>
                </td>
              </tr>
            `
            : ''
            }
          ${this.groupQuery.result?.data?.files.map(file => html`
            <tr>
              <td><div>${file.FileName}</div></td>
              <td><div>${file.CreationDate?.toLocaleString()}</div></td>
              <td><div>${file.EstimatedFileSize ? formatBytes(file.EstimatedFileSize) : ''}</div></td>
              <td>
                <div style="justify-content: flex-end;">
                  <sl-dropdown>
                    <sl-button slot="trigger" size="small">
                      <sl-icon name="three-dots" label="Actions"></sl-icon>
                    </sl-button>
                    <sl-menu>
                      <sl-menu-label>Row Actions</sl-menu-label>
                      <ep-event-proxy events="click">
                        <a slot="proxy" href="${file.ContentDownloadUrl ?? '#'}" target="_blank">Download</a>
                        <sl-menu-item slot="display">
                          <sl-icon slot="prefix" name="cloud-download"></sl-icon>
                          Download
                        </sl-menu-item>
                      </ep-event-proxy>
                      <sl-divider></sl-divider>
                      <sl-menu-item class="text-danger"
                        ?disabled=${true}
                        @click=${() => {}}
                        >
                        <sl-icon slot="prefix" name="trash"></sl-icon>
                        <span>Delete</span>
                      </sl-menu-item>
                    </sl-menu>
                  </sl-dropdown>
                </div>
              </td>
            </tr>
          `)}
        </tbody>
      </table>
      </div>
    `
    //#endregion
  }

  //#endregion

  //#region Styles
  static styles = [stylesBootstrap, stylesFluentTable, css`
    :host {
      display: inline-block;
      width: auto;
      font-family: var(--bs-body-font-family);
      font-size: var(--bs-body-font-size);
      font-weight: var(--bs-body-font-weight);
      line-height: var(--bs-body-line-height);
      color: var(--bs-body-color);
      --font-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, "Cascadia Mono", Consolas, Liberation Mono, monospace;
    }
    table.fluent thead tr th {
      white-space: normal;
    }
    table.fluent tbody td {
      white-space: normal;
    }
    table.fluent tbody td .ellipsis {
      display: block;
      overflow: hidden!important;
      white-space: nowrap!important;
      text-overflow: ellipsis!important;
    }
  `]
  //#endregion
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-file-reference-group-display': EPFileReferenceGroupDisplay
  }
}
